import { Draggable } from '../internalized/Draggable'
import React from 'react'
import styled from 'styled-components'
import { Vector2 } from 'three'

import { VizTooltipContainer } from './VizTooltipContainer'

const FloatingContainer = styled.div<{ $isFrozen: boolean }>`
  position: absolute;
  z-index: 999;
  flex-grow: 1;
  pointer-events: ${({ $isFrozen }) => ($isFrozen ? 'auto' : 'none')};
`

/**
 * A "temporary" generic wrapper component for using the tooltip component.
 * To be replaced by Gecko UI's tooltip component in the future when standardized.
 *
 * This should be used for use cases where the tooltip is NOT hovering over an SVG element.
 */
export const VizTooltip: React.FC<{
  isVisible: boolean
  isFrozen?: boolean
  children: React.ReactNode
}> = ({ isVisible, isFrozen, children }) => {
  const draggableRef = React.useRef<HTMLDivElement>(null)
  const contentRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const onMouseMove = (event: PointerEvent) => {
      if (isFrozen) return
      if (draggableRef.current) {
        const offset = new Vector2(20, 10)
        let sizedParent = draggableRef.current.offsetParent
        let parentRect = sizedParent?.getBoundingClientRect()
        while (
          sizedParent &&
          (sizedParent as HTMLElement).offsetParent &&
          parentRect?.width === 0
        ) {
          sizedParent = (sizedParent as HTMLElement).offsetParent
          parentRect = sizedParent?.getBoundingClientRect()
        }
        parentRect ??= new DOMRect(0, 0, 0, 0)
        const contentRect =
          contentRef.current?.getBoundingClientRect() ?? new DOMRect(0, 0, 0, 0)
        let x = event.clientX - parentRect.left + offset.x
        if (x > parentRect.width / 2) x -= contentRect.width + offset.x * 2
        let y =
          event.clientY - parentRect.top + offset.y - contentRect.height / 2

        if (x < 0) x = 0
        if (y < 0) y = 0
        if (x + contentRect.width > parentRect.width)
          x = parentRect.width - contentRect.width
        if (y + contentRect.height > parentRect.height)
          y = parentRect.height - contentRect.height

        draggableRef.current.style.left = `${x}px`
        draggableRef.current.style.top = `${y}px`
      }
    }
    document.addEventListener('pointermove', onMouseMove)
    return () => document.removeEventListener('pointermove', onMouseMove)
  }, [isFrozen])

  return (
    <Draggable
      sharedRef={draggableRef}
      style={{ visibility: isVisible ? 'visible' : 'hidden' }}>
      <FloatingContainer ref={contentRef} $isFrozen={isFrozen ?? false}>
        <VizTooltipContainer isFrozen={isFrozen}>
          {children}
        </VizTooltipContainer>
      </FloatingContainer>
    </Draggable>
  )
}
